import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useSnackbar } from 'notistack';
import { X, Loader2, Shield, User, UserPlus } from 'lucide-react';
import { api } from '../../../services/api';

interface GrupoCargo {
  id?: string;
  id_grupo: string;
  cargo: string;
  descricao: string;
  admin: boolean;
  responsavel: boolean;
}

interface CargoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  grupoId: string;
  cargoToEdit?: GrupoCargo | null;
}

export function CargoModal({
  open,
  onClose,
  onSuccess,
  grupoId,
  cargoToEdit
}: CargoModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<GrupoCargo>({
    id_grupo: grupoId,
    cargo: '',
    descricao: '',
    admin: false,
    responsavel: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (cargoToEdit) {
      setFormData(cargoToEdit);
    } else {
      setFormData({
        id_grupo: grupoId,
        cargo: '',
        descricao: '',
        admin: false,
        responsavel: false,
      });
    }
    setErrors({});
  }, [cargoToEdit, grupoId, open]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.cargo.trim()) {
      newErrors.cargo = 'Nome do cargo é obrigatório';
    } else if (formData.cargo.length < 2) {
      newErrors.cargo = 'Nome do cargo deve ter pelo menos 2 caracteres';
    } else if (formData.cargo.length > 50) {
      newErrors.cargo = 'Nome do cargo deve ter no máximo 50 caracteres';
    }

    // Descrição é opcional, mas se preenchida, deve ter tamanho válido
    if (formData.descricao.trim() && formData.descricao.length < 5) {
      newErrors.descricao = 'Descrição deve ter pelo menos 5 caracteres';
    } else if (formData.descricao.length > 200) {
      newErrors.descricao = 'Descrição deve ter no máximo 200 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (cargoToEdit?.id) {
        // Editar cargo existente
        await api.put(`/grupos-cargos/${cargoToEdit.id}`, {
          cargo: formData.cargo,
          descricao: formData.descricao,
          admin: formData.admin,
          responsavel: formData.responsavel,
        });
        enqueueSnackbar('Cargo atualizado com sucesso!', { variant: 'success' });
      } else {
        // Criar novo cargo
        await api.post('/grupos-cargos', formData);
        enqueueSnackbar('Cargo criado com sucesso!', { variant: 'success' });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar cargo:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao salvar cargo',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof GrupoCargo) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = (field === 'admin' || field === 'responsavel') && event.target instanceof HTMLInputElement
      ? event.target.checked
      : event.target.value;

    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md z-50">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-foreground">
              {cargoToEdit ? 'Editar Cargo' : 'Novo Cargo'}
            </Dialog.Title>
            <Dialog.Close
              className="text-muted-foreground hover:text-foreground transition-colors"
              disabled={loading}
            >
              <X size={20} />
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Nome do Cargo */}
            <div>
              <label htmlFor="cargo" className="block text-sm font-medium text-foreground mb-1">
                Nome do Cargo *
              </label>
              <input
                id="cargo"
                type="text"
                value={formData.cargo}
                onChange={handleInputChange('cargo')}
                placeholder="Ex: Comandante, Instrutor, Membro"
                disabled={loading}
                autoFocus
                className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.cargo
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-border'
                }`}
              />
              {errors.cargo && (
                <p className="mt-1 text-sm text-red-600">{errors.cargo}</p>
              )}
              {!errors.cargo && (
                <p className="mt-1 text-sm text-muted-foreground">Ex: Comandante, Instrutor, Membro</p>
              )}
            </div>

            {/* Descrição */}
            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-foreground mb-1">
                Descrição
              </label>
              <textarea
                id="descricao"
                rows={3}
                value={formData.descricao}
                onChange={handleInputChange('descricao')}
                placeholder="Descreva as responsabilidades deste cargo"
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                  errors.descricao
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-border'
                }`}
              />
              {errors.descricao && (
                <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
              )}
              {!errors.descricao && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Descreva as responsabilidades deste cargo
                </p>
              )}
            </div>

            {/* Cargo Administrativo */}
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.admin}
                  onChange={handleInputChange('admin')}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-foreground flex items-center space-x-2">
                  {formData.admin ? <Shield size={16} /> : <User size={16} />}
                  <span>Cargo Administrativo</span>
                </span>
              </label>
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>ℹ️ Informação:</strong> Cargos administrativos possuem permissões especiais de gerenciamento do grupo
                </p>
              </div>
            </div>

            {/* Cargo de Responsável */}
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.responsavel}
                  onChange={handleInputChange('responsavel')}
                  disabled={loading}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-foreground flex items-center space-x-2">
                  {formData.responsavel ? <UserPlus size={16} /> : <User size={16} />}
                  <span>Cargo de Responsável</span>
                </span>
              </label>
              <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/30 rounded-md">
                <p className="text-sm text-green-800 dark:text-green-300">
                  <strong>ℹ️ Informação:</strong> Cargos de responsável são designados para coordenar e liderar equipes específicas
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              <span>{loading ? 'Salvando...' : cargoToEdit ? 'Atualizar' : 'Criar'}</span>
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
